import {groupByUniqueKey} from '@benchling/js-utils';
import {Button, createStyles, Tooltip} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import AnnotationTable from 'molbio/annotations/AnnotationTable';
import AnnotationTypeVisibilityDropdown from 'molbio/annotations/AnnotationTypeVisibilityDropdown';
import {AnnotationData, getUniqueNonEmptyAnnotationTypes} from 'molbio/annotations/AnnotationUtil';
import {FONT_PROXIMA_19} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

import AnnotationBulkEditModal, {BulkEditAnnotationProperties} from './AnnotationBulkEditModal';

const useStyles = createStyles((theme) => ({
  tabContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabHeaderContainer: {
    display: 'flex',
    alignItems: 'baseline',
  },
  // preexisting EXTRA LARGE TEXT
  tabHeader: FONT_PROXIMA_19,
  tabSubheader: {
    color: theme.colors.gray[7],
    marginLeft: theme.spacing.md,
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.md,
  },
  buttonsContainer: {
    display: 'flex',
  },
  partsInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing.sm,
    color: GRAY_DARKER,
  },
  partsInfoText: {
    marginBottom: 0,
    marginRight: theme.spacing.xs,
  },
}));

export interface ExpandedAnnotationTabProps {
  annotations: Array<AnnotationData>;
  allowFeatureEdits: boolean;
  saveAnnotationsAsFeatures: (cids: Array<string>) => void;
  // Callback to run CRISPR on the selected annotations, or null if CRISPR is not supported.
  runCrispr: ((cids: Array<string>) => void) | null;
  deleteAnnotations: (cids: Array<string>) => void;
  bulkEditAnnotations: (cids: Array<string>, updatedPropertie: BulkEditAnnotationProperties) => void;
  crisprInProgress: boolean;
  isSingleStranded: boolean;
  onToggleVisibility: (cids: Array<string>, visibility: boolean) => void;
}

/**
 * Renders the expanded annotations tab view.
 *
 * This includes a selectable table of annotations and some bulk actions that can be taken on those annotations.
 * @param annotations List of annotations to display.
 * @param saveAnnotationsAsFeatures Handles saving selected annotations to feature library
 * @param runCrispr Handles designing CRISPR guides for selected annotations.
 * @param deleteAnnotations Handles bulk deleting selected annotations.
 */
export default function ExpandedAnnotationTab({
  annotations,
  allowFeatureEdits,
  saveAnnotationsAsFeatures,
  runCrispr,
  deleteAnnotations,
  bulkEditAnnotations,
  crisprInProgress,
  isSingleStranded,
  onToggleVisibility,
}: ExpandedAnnotationTabProps): JSX.Element {
  const {classes} = useStyles();
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const annotationsByCid = useMemo(
    () => groupByUniqueKey(annotations, (annotation) => annotation.cid),
    [annotations]
  );
  const validSelectedIds = useMemo(
    () => selectedIds.filter((id) => annotationsByCid[id] != null),
    [selectedIds, annotationsByCid]
  );
  // If sequence features are editable, any uneditable annotations are hoisted from parts
  const selectedPartAnnotationIds = validSelectedIds.filter((id) => !annotationsByCid[id].editable);
  const hiddenAnnotationsCount = annotations.filter(({visible}) => !visible).length;
  const annotationIds = useMemo(() => new Set(annotations.map(({cid}) => cid)), [annotations]);

  const canEditAnnotations =
    allowFeatureEdits && validSelectedIds.length > 0 && selectedPartAnnotationIds.length === 0;
  const canDesignCRISPR = allowFeatureEdits && validSelectedIds.length > 0 && !crisprInProgress;

  useEffect(() => {
    if (!allowFeatureEdits) {
      setSelectedIds([]);
    }
  }, [allowFeatureEdits]);

  useEffect(() => {
    setSelectedIds((previousSelectedIds) => {
      const validIds = previousSelectedIds.filter((id) => annotationIds.has(id));
      return validIds.length === previousSelectedIds.length ? previousSelectedIds : validIds;
    });
  }, [annotationIds]);

  return (
    <div
      className={classes.tabContainer}
      {...getTestProps({component: 'ExpandedAnnotationTab', element: 'root'})}
    >
      <div className={classes.tabHeaderContainer}>
        <p className={classes.tabHeader}>Annotations</p>
        <p className={classes.tabSubheader}>{`${annotations.length} total${
          hiddenAnnotationsCount > 0 ? `, ${hiddenAnnotationsCount} hidden` : ''
        }`}</p>
      </div>
      <div className={classes.actionsContainer}>
        <ButtonGroup>
          <AnnotationTypeVisibilityDropdown
            annotations={annotations}
            onToggleVisibility={onToggleVisibility}
          />
        </ButtonGroup>
        <div className={classes.buttonsContainer}>
          {selectedPartAnnotationIds.length > 0 && (
            <div className={classes.partsInfoContainer}>
              <p className={classes.partsInfoText}>{`${selectedPartAnnotationIds.length} ${pluralize(
                selectedPartAnnotationIds.length,
                'part annotation'
              )} selected`}</p>
              <Tooltip label="Cannot edit or delete annotations that are inside parts">
                <BenchlingIcon icon="info-circle" />
              </Tooltip>
            </div>
          )}
          <Button.Group>
            <Tooltip label="Edit" withinPortal={true}>
              <Button
                aria-label="Edit"
                {...getDisabledTooltipButtonProps(!canEditAnnotations, () => {
                  if (
                    !allowFeatureEdits ||
                    validSelectedIds.length === 0 ||
                    selectedPartAnnotationIds.length > 0
                  ) {
                    return;
                  }
                  legacyRunModal(AnnotationBulkEditModal, {
                    selectedAnnotations: validSelectedIds.map((id) => annotationsByCid[id]),
                    isSingleStranded,
                    allAnnotationTypes: getUniqueNonEmptyAnnotationTypes(annotations),
                    bulkEditAnnotations: (updatedProperties: BulkEditAnnotationProperties) => {
                      bulkEditAnnotations(validSelectedIds, updatedProperties);
                    },
                  });
                })}
                size="sm"
                variant="tertiary"
              >
                <BenchlingIcon icon="edit" />
              </Button>
            </Tooltip>
            <Tooltip label="Save to feature library" withinPortal={true}>
              <Button
                aria-label="Save to feature library"
                size="sm"
                variant="tertiary"
                {...getDisabledTooltipButtonProps(validSelectedIds.length === 0, () => {
                  saveAnnotationsAsFeatures(validSelectedIds);
                })}
              >
                <BenchlingIcon icon="save" />
              </Button>
            </Tooltip>
            {runCrispr && (
              <Tooltip label="Design CRISPR guides" withinPortal={true}>
                <Button
                  aria-label="Design CRISPR guides"
                  size="sm"
                  variant="tertiary"
                  {...getDisabledTooltipButtonProps(!canDesignCRISPR, () => {
                    runCrispr(validSelectedIds);
                  })}
                >
                  <BenchlingIcon icon="crispr" />
                </Button>
              </Tooltip>
            )}
            <Tooltip label="Delete" withinPortal={true}>
              <Button
                aria-label="Delete"
                size="sm"
                variant="warn"
                {...getDisabledTooltipButtonProps(!canEditAnnotations, () => {
                  deleteAnnotations(validSelectedIds);
                  setSelectedIds([]);
                })}
              >
                <BenchlingIcon icon="delete" />
              </Button>
            </Tooltip>
          </Button.Group>
        </div>
      </div>
      <AnnotationTable
        annotations={annotations}
        selectedIds={validSelectedIds}
        setSelectedIds={setSelectedIds}
        allowFeatureEdits={allowFeatureEdits}
        onToggleVisibility={onToggleVisibility}
      />
    </div>
  );
}
